import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Trophy, Settings, Sparkles, Star, Map } from 'lucide-react'
import type { StudentProgress, Adventure } from '../../types'
import AgeSelector from '../UI/AgeSelector'
import AdventureCard from '../UI/AdventureCard'
import { allAdventures, getAdventuresByAge } from '../../data/adventures'

interface HomeViewProps {
  progress: StudentProgress
  onStartAdventure: (adventure: Adventure) => void
  onViewProgress: () => void
  onSetAge: (age: '3' | '4' | '5') => void
}

export default function HomeView({ progress, onStartAdventure, onViewProgress, onSetAge }: HomeViewProps) {
  const [showAgeSelector, setShowAgeSelector] = useState(!progress.currentAge)

  const filteredAdventures = useMemo(() => {
    if (!progress.currentAge) return allAdventures
    return getAdventuresByAge(progress.currentAge)
  }, [progress.currentAge])

  if (showAgeSelector) {
    return (
      <AgeSelector
        onSelect={(age) => {
          onSetAge(age)
          setShowAgeSelector(false)
        }}
      />
    )
  }

  const completedCount = progress.completedAdventures.length
  const totalAdventures = filteredAdventures.length

  return (
    <div className="h-full flex flex-col safe-top safe-bottom bg-[var(--math-bg)]">
      {/* Header */}
      <header className="px-5 py-4 flex items-center justify-between">
        <motion.div 
          className="flex items-center gap-3"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="w-10 h-10 bg-gradient-to-br from-[var(--math-primary)] to-[#FF8C42] rounded-xl flex items-center justify-center shadow-md">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-gray-800 text-lg leading-tight">Math Adventures</h1>
            <p className="text-xs text-gray-500">Learn with Tilly</p>
          </div>
        </motion.div>
        
        <div className="flex items-center gap-2">
          <motion.a
            href="/prototypes/flow2/roadmap.html"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm"
            title="View roadmap"
            aria-label="View product roadmap"
          >
            <Map className="w-5 h-5 text-[var(--math-secondary)]" />
          </motion.a>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onViewProgress}
            className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm"
            aria-label="View progress"
          >
            <Trophy className="w-5 h-5 text-[var(--math-accent)]" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAgeSelector(true)}
            className="h-10 px-3 bg-white rounded-xl flex items-center gap-2 shadow-sm text-sm font-medium text-gray-700"
          >
            <span className="w-6 h-6 bg-[var(--math-secondary)]/20 rounded-lg flex items-center justify-center text-xs font-bold text-[var(--math-secondary)]">
              {progress.currentAge}
            </span>
            <Settings className="w-4 h-4 text-gray-400" />
          </motion.button>
        </div>
      </header>

      {/* Progress Banner */}
      {completedCount > 0 && (
        <motion.div 
          className="mx-5 mb-4 bg-gradient-to-r from-[var(--math-success)]/10 to-[var(--math-accent)]/10 rounded-2xl p-4"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[var(--math-success)] rounded-xl flex items-center justify-center">
                <Star className="w-6 h-6 text-white fill-white" />
              </div>
              <div>
                <p className="font-bold text-gray-800">Keep it up!</p>
                <p className="text-sm text-gray-500">{completedCount} of {totalAdventures} completed</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-[var(--math-success)]">
                {totalAdventures > 0 ? Math.round((completedCount / totalAdventures) * 100) : 0}%
              </span>
            </div>
          </div>
          <div className="mt-3 h-2 bg-white/50 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-[var(--math-success)] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${totalAdventures > 0 ? (completedCount / totalAdventures) * 100 : 0}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto px-5 py-2 no-scrollbar">
        <motion.h2 
          className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          Choose an Adventure
          <Sparkles className="w-5 h-5 text-[var(--math-accent)]" />
        </motion.h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-24">
          {filteredAdventures.map((adventure, index) => (
            <motion.div
              key={adventure.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
            >
              <AdventureCard
                adventure={adventure}
                isCompleted={progress.completedAdventures.some(
                  a => a.adventureId === adventure.id
                )}
                onClick={() => onStartAdventure(adventure)}
              />
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  )
}
